import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { LogOut, UserCheck, Search, FileText, MessageSquare, BarChart3, ArrowDown, ArrowRight, Check, SlidersHorizontal, HelpCircle, DollarSign, Banknote, LayoutGrid, List as ListIcon } from 'lucide-react'
import AppHeader from '../components/AppHeader'
import { loanApi, type ComparisonResult, type PolicyFilters } from '../api/loanApi'
import { POLICY_TAG_CATEGORIES, CUSTOMER_FILTER_CATEGORIES, CUSTOMER_CHECKBOX_CATEGORIES } from '../constants/policyTags'
import LeadCaptureModal from '../components/LeadCaptureModal'
import ProfileCompletionBadge from '../components/ProfileCompletionBadge'
import { profileApi } from '../api/profileApi'
import { useAuth } from '../contexts/AuthContext'
import { getMyConnections, deleteConnection, getCustomerApplication, type Connection } from '../api/brokerApi'
import ShareWithBrokerCard from '../components/ShareWithBrokerCard'
import ApplicationStatusWidget from '../components/ApplicationStatusWidget'
import { saveComparison, getSavedComparisons, deleteSavedComparison, type SavedComparison } from '../api/comparisonsApi'
import { getCashFlow, getFinancialsSharedStatus, type CashFlowSummary } from '../api/incomeExpenseApi'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'

export default function CustomerPortal() {
  const [loanAmount, setLoanAmount] = useState('500000')
  const [propertyValue, setPropertyValue] = useState('700000')
  const [loanTerm, setLoanTerm] = useState('30')
  const [results, setResults] = useState<ComparisonResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [lvr, setLvr] = useState(0)
  const [sortBy, setSortBy] = useState<'total_cost' | 'interest_rate' | 'monthly_payment' | 'total_fees' | 'comparison_rate'>('total_cost')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterFeatures, setFilterFeatures] = useState<string[]>([])
  const [filterBank, setFilterBank] = useState<string>('all')
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false)
  const [faviconErrors, setFaviconErrors] = useState<Record<string, boolean>>({})

  // View toggle and comparison selection (Phase 3)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')


  // Policy filter state (Epic 9)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [policyFilters, setPolicyFilters] = useState<PolicyFilters>({})
  const [hasSearchedWithFilters, setHasSearchedWithFilters] = useState(false)
  const [cashFlowSummary, setCashFlowSummary] = useState<CashFlowSummary | null>(null)
  const [financialsShared, setFinancialsShared] = useState(false)

  const activeFilterCount = Object.values(policyFilters).reduce(
    (count, vals) => count + (vals?.length || 0), 0
  )

  // Convert raw filter keys to human-readable labels for broker email
  const buildSituationContext = (filters: PolicyFilters): Record<string, string[]> | undefined => {
    const result: Record<string, string[]> = {}
    for (const [catKey, selectedValues] of Object.entries(filters)) {
      if (!selectedValues || selectedValues.length === 0) continue
      const category = POLICY_TAG_CATEGORIES[catKey]
      if (!category) continue
      const labels = selectedValues.map(v => category.tags.find(t => t.value === v)?.label || v)
      if (labels.length > 0) result[category.label] = labels
    }
    return Object.keys(result).length > 0 ? result : undefined
  }

  const setPolicyDropdown = (category: string, value: string) => {
    setPolicyFilters(prev => {
      const next = { ...prev }
      if (value) {
        next[category] = [value]
      } else {
        delete next[category]
      }
      return next
    })
  }

  const togglePolicyCheckbox = (category: string, value: string, checked: boolean) => {
    setPolicyFilters(prev => {
      const current = prev[category] || []
      const updated = checked ? [...current, value] : current.filter(v => v !== value)
      const next = { ...prev }
      if (updated.length > 0) {
        next[category] = updated
      } else {
        delete next[category]
      }
      return next
    })
  }

  const clearAllPolicyFilters = () => {
    setPolicyFilters({})
    setHasSearchedWithFilters(false)
  }
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedLoan, setSelectedLoan] = useState<ComparisonResult | null>(null)
  const [expandedShareLoan, setExpandedShareLoan] = useState<string | null>(null)

  // Saved comparisons state (Epic 6)
  const [savedComparisons, setSavedComparisons] = useState<SavedComparison[]>([])
  const [savingComparison, setSavingComparison] = useState(false)
  const [saveComparisonName, setSaveComparisonName] = useState('')
  const [showSaveModal, setShowSaveModal] = useState(false)

  // Profile completion for OAuth customers
  const { isAuthenticated, user, getAccessToken, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Warm up backend on page load to avoid cold-start hang on first compare
  useEffect(() => {
    loanApi.warmup()
  }, [])

  // Handle return from OAuth with pending lead modal (Sign in to pre-fill flow)
  useEffect(() => {
    const state = location.state as { openLeadModal?: boolean; loanDetails?: object; loanProductId?: string; selectedLoan?: ComparisonResult } | null
    if (state?.openLeadModal && state.selectedLoan) {
      setSelectedLoan(state.selectedLoan)
      setIsModalOpen(true)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])
  const [profileCompletion, setProfileCompletion] = useState<{
    completion_percent: number
    missing: string[]
  } | null>(null)
  const [customerPhone, setCustomerPhone] = useState<string | undefined>()
  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'customer' || !getAccessToken()) {
      setProfileCompletion(null)
      setCustomerPhone(undefined)
      return
    }
    const token = getAccessToken()!
    profileApi.getProfileCompletion(token).then(setProfileCompletion).catch(() => setProfileCompletion(null))
    profileApi.getCustomerProfile(token).then((res) => {
      setCustomerPhone(res.profile.phone_number || undefined)
    }).catch(() => {})
  }, [isAuthenticated, user?.userType, getAccessToken])

  // Broker connection for authenticated customers
  const [myConnection, setMyConnection] = useState<Connection | null>(null)
  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'customer' || !getAccessToken()) return
    getMyConnections(getAccessToken()!)
      .then((res) => {
        const active = res.connections.find((c) => c.status === 'active' || c.status === 'pending')
        setMyConnection(active || null)
      })
      .catch(() => setMyConnection(null))
  }, [isAuthenticated, user?.userType, getAccessToken])

  // Saved comparisons (Epic 6)
  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'customer' || !getAccessToken()) return
    getSavedComparisons(getAccessToken()!)
      .then(setSavedComparisons)
      .catch(() => setSavedComparisons([]))
  }, [isAuthenticated, user?.userType, getAccessToken])

  // Financial profile summary for dashboard card
  useEffect(() => {
    if (!isAuthenticated || user?.userType !== 'customer' || !getAccessToken()) return
    const token = getAccessToken()!
    getCashFlow(token).then(setCashFlowSummary).catch(() => setCashFlowSummary(null))
    getFinancialsSharedStatus(token).then((res) => setFinancialsShared(res.shared)).catch(() => {})
  }, [isAuthenticated, user?.userType, getAccessToken])

  const handleSaveComparison = async () => {
    if (!getAccessToken() || !results.length) return
    setSavingComparison(true)
    try {
      await saveComparison(getAccessToken()!, {
        name: saveComparisonName || `Comparison ${new Date().toLocaleDateString('en-AU')}`,
        criteria: {
          loan_amount: parseFloat(loanAmount),
          property_value: parseFloat(propertyValue),
          loan_term_years: parseInt(loanTerm),
          policy_filters: policyFilters,
        },
        results: results.map(r => ({
          product_name: r.product_name,
          product_type: r.product_type,
          interest_rate: r.interest_rate,
          monthly_payment: r.monthly_payment,
          total_cost: r.total_cost,
        })),
      })
      setShowSaveModal(false)
      setSaveComparisonName('')
      // Reload list
      const updated = await getSavedComparisons(getAccessToken()!)
      setSavedComparisons(updated)
    } catch (err) {
      console.error('Failed to save comparison:', err)
    } finally {
      setSavingComparison(false)
    }
  }

  const handleDeleteSavedComparison = async (id: string) => {
    if (!getAccessToken()) return
    try {
      await deleteSavedComparison(getAccessToken()!, id)
      setSavedComparisons(prev => prev.filter(c => c.id !== id))
    } catch (err) {
      console.error('Failed to delete comparison:', err)
    }
  }

  const handleRerunComparison = (comparison: SavedComparison) => {
    const c = comparison.criteria as { loan_amount?: number; property_value?: number; loan_term_years?: number; policy_filters?: PolicyFilters }
    if (c.loan_amount) setLoanAmount(String(c.loan_amount))
    if (c.property_value) setPropertyValue(String(c.property_value))
    if (c.loan_term_years) setLoanTerm(String(c.loan_term_years))
    if (c.policy_filters) setPolicyFilters(c.policy_filters)
    // Scroll to compare section and trigger compare
    document.getElementById('compare-section')?.scrollIntoView({ behavior: 'smooth' })
    setTimeout(() => runCompare(c.policy_filters || {}), 300)
  }

  const handleDisconnect = async () => {
    if (!myConnection || !getAccessToken()) return
    const token = getAccessToken()!

    // Check if customer has an active application — warn them it will be withdrawn
    let hasActiveApp = false
    try {
      const app = await getCustomerApplication(token)
      hasActiveApp = app.is_active
    } catch {
      // No application or error — proceed with simple confirm
    }

    const message = hasActiveApp
      ? 'You have an active loan application with this broker. Disconnecting will withdraw your application. Are you sure?'
      : 'Are you sure you want to disconnect from this broker?'
    if (!confirm(message)) return

    try {
      await deleteConnection(token, myConnection.id)
      setMyConnection(null)
    } catch (err) {
      console.error('Failed to disconnect:', err)
    }
  }

  // Shared compare logic — used by both "Compare Loans" and "Compare with Filters"
  const runCompare = async (filters: PolicyFilters) => {
    setLoading(true)
    setError('')

    try {
      const hasFilters = Object.values(filters).some(v => v && v.length > 0)
      const request = {
        loan_amount: parseFloat(loanAmount),
        property_value: parseFloat(propertyValue),
        loan_term_years: parseInt(loanTerm),
        ...(hasFilters ? { filters } : {}),
      }

      const response = await loanApi.compareLoans(request)

      if (response.error) {
        setError(response.error)
        return
      }
      setResults(response.results)
      setLvr(response.search_criteria?.lvr ?? (parseFloat(loanAmount) / parseFloat(propertyValue)) * 100)

      // Store comparison snapshot for broker connection auto-attach
      sessionStorage.setItem('last_comparison', JSON.stringify({
        loan_amount: parseFloat(loanAmount),
        property_value: parseFloat(propertyValue),
        loan_term_years: parseInt(loanTerm),
        lvr: response.search_criteria?.lvr ?? (parseFloat(loanAmount) / parseFloat(propertyValue)) * 100,
        results_count: response.results.length,
        top_results: response.results.slice(0, 5).map(r => ({
          product_name: r.product_name,
          interest_rate: r.interest_rate,
          comparison_rate: r.comparison_rate,
          monthly_payment: r.monthly_payment,
          total_cost: r.total_cost,
        })),
        compared_at: new Date().toISOString(),
      }))
    } catch (err) {
      if (err instanceof Error && err.message === 'TIMEOUT') {
        setError('Server is waking up — please try again in a few seconds.')
      } else {
        setError('Failed to compare loans. Please try again.')
      }
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  // "Compare Loans" button — never sends filters
  const handleCompare = async (e: React.FormEvent) => {
    e.preventDefault()
    setHasSearchedWithFilters(false)
    setHasSearched(true)
    await runCompare({})
  }

  // "Compare with Filters" button inside Advanced Search panel
  const handleAdvancedCompare = async () => {
    const hasFilters = activeFilterCount > 0
    setHasSearchedWithFilters(hasFilters)
    setHasSearched(true)
    await runCompare(policyFilters)
  }

  // Known bank identifiers: prefix → { name, domain }
  const bankLookup: Record<string, { name: string; domain: string }> = {
    'Commonwealth Bank': { name: 'Commonwealth Bank', domain: 'commbank.com.au' },
    'CBA': { name: 'Commonwealth Bank', domain: 'commbank.com.au' },
    'Westpac': { name: 'Westpac', domain: 'westpac.com.au' },
    'ANZ': { name: 'ANZ', domain: 'anz.com.au' },
    'NAB': { name: 'NAB', domain: 'nab.com.au' },
    'Macquarie Bank': { name: 'Macquarie Bank', domain: 'macquarie.com.au' },
    'Macquarie': { name: 'Macquarie Bank', domain: 'macquarie.com.au' },
    'ING': { name: 'ING', domain: 'ing.com.au' },
    'Suncorp': { name: 'Suncorp', domain: 'suncorp.com.au' },
    'Bankwest': { name: 'Bankwest', domain: 'bankwest.com.au' },
    'St George': { name: 'St George', domain: 'stgeorge.com.au' },
    'Bank of Melbourne': { name: 'Bank of Melbourne', domain: 'bankofmelbourne.com.au' },
  }

  // Extract bank name from product name — handles "Bank - Product" and "BANK Product" formats
  const getBankInfo = (productName: string): { name: string; domain: string } => {
    // Try "Bank Name - Product Name" format first
    const parts = productName.split(' - ')
    if (parts.length > 1) {
      const key = parts[0].trim()
      if (bankLookup[key]) return bankLookup[key]
      return { name: key, domain: key.toLowerCase().replace(/\s+/g, '') + '.com.au' }
    }
    // Try matching known bank prefixes in the product name
    for (const [prefix, info] of Object.entries(bankLookup)) {
      if (productName.startsWith(prefix + ' ')) return info
    }
    return { name: 'Unknown', domain: '' }
  }

  const getBankName = (productName: string) => getBankInfo(productName).name
  const getBankDomain = (productName: string) => getBankInfo(productName).domain

  // Human-readable product type labels
  const TYPE_LABELS: Record<string, string> = {
    variable: 'Variable',
    fixed_1: 'Fixed 1 Year',
    fixed_2: 'Fixed 2 Year',
    fixed_3: 'Fixed 3 Year',
    fixed_5: 'Fixed 5 Year',
    variable_owner_occupier: 'Variable (Owner Occ)',
    variable_investor: 'Variable (Investor)',
    fixed_owner_occupier: 'Fixed (Owner Occ)',
    fixed_investor: 'Fixed (Investor)',
    split: 'Split Loan',
    low_rate_variable: 'Low Rate Variable',
  }
  const getTypeLabel = (type: string) => TYPE_LABELS[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

  // Derive unique banks and product types for filter options
  const availableBanks = [...new Set(results.map(r => getBankName(r.product_name)))].filter(b => b !== 'Unknown').sort()
  const availableTypes = [...new Set(results.map(r => r.product_type))].sort()

  const sortedResults = results
    .filter(r => {
      if (filterType !== 'all' && r.product_type !== filterType) return false
      if (filterBank !== 'all' && getBankName(r.product_name) !== filterBank) return false
      if (filterFeatures.length > 0) {
        for (const f of filterFeatures) {
          if (!r.features[f]) return false
        }
      }
      return true
    })
    .sort((a, b) => {
      let diff = 0
      switch (sortBy) {
        case 'interest_rate':
          diff = a.interest_rate - b.interest_rate; break
        case 'monthly_payment':
          diff = a.monthly_payment - b.monthly_payment; break
        case 'total_fees':
          diff = a.total_fees - b.total_fees; break
        case 'comparison_rate':
          diff = a.comparison_rate - b.comparison_rate; break
        default:
          diff = a.total_cost - b.total_cost
      }
      // Tiebreaker: eligible sorts before may_be_eligible (only when badges visible)
      if (diff === 0 && hasSearchedWithFilters) {
        const rank = (m?: string) => m === 'eligible' ? 0 : 1
        diff = rank(a.policy_match) - rank(b.policy_match)
      }
      return sortDir === 'desc' ? -diff : diff
    })

  // Identify cards with special badges
  const lowestRateId = results.length > 0
    ? [...results].sort((a, b) => a.interest_rate - b.interest_rate)[0].id
    : null
  const lowestPaymentId = results.length > 0
    ? [...results].sort((a, b) => a.monthly_payment - b.monthly_payment)[0].id
    : null

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        rightContent={
          <>
            <span className="text-gray-600">Customer Portal</span>
            {isAuthenticated && user?.userType === 'customer' && (
              <Link to="/customer/profile" className="text-sm text-primary-600 hover:text-primary-700">
                My Profile
              </Link>
            )}
            <Link to="/brokers" className="text-sm text-primary-600 hover:text-primary-700">
              Find a Broker
            </Link>
            <Link to="/broker" className="text-sm text-primary-600 hover:text-primary-700">
              Switch to Broker View
            </Link>
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {user?.full_name || user?.username || user?.email || 'User'}
                </span>
                <button
                  onClick={logout}
                  className="text-gray-600 hover:text-red-600 transition-colors"
                  title="Logout"
                >
                  <LogOut size={20} />
                </button>
              </div>
            ) : (
              <Link to="/login" className="btn-primary text-sm py-2 px-4">
                Sign In
              </Link>
            )}
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ── Dashboard Section (authenticated customers only) ── */}
        {isAuthenticated && user?.userType === 'customer' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Your Dashboard</h1>
              <p className="text-gray-600 mt-2">
                Welcome back, {user?.full_name || user?.username || 'there'}
              </p>
            </div>

            {profileCompletion && profileCompletion.completion_percent < 100 && (
              <div className="mb-6">
                <ProfileCompletionBadge
                  completionPercent={profileCompletion.completion_percent}
                  missing={profileCompletion.missing}
                  userRole="customer"
                />
              </div>
            )}

            {/* Application Progress Widget — prominent, full-width */}
            <div className="mb-6 max-w-2xl mx-auto">
              <ApplicationStatusWidget token={getAccessToken()!} />
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {/* Broker Connection Card */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Broker Connection</h3>
                </div>
                {myConnection ? (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">
                      {myConnection.status === 'active'
                        ? `Connected with ${myConnection.broker_name || 'your broker'}`
                        : `Pending — waiting for ${myConnection.broker_name || 'broker'} to respond`}
                    </p>
                    <button
                      onClick={handleDisconnect}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600 mb-3">No broker connected yet</p>
                    <Link to="/brokers" className="text-sm px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-block">
                      Find a Broker
                    </Link>
                  </div>
                )}
              </div>

              {/* Compare Loans Quick-Access Card */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Compare Loans</h3>
                </div>
                <p className="text-sm text-gray-600 mb-3">Search and compare home loan products from Australia's top lenders</p>
                <button
                  onClick={() => document.getElementById('compare-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <ArrowDown className="w-4 h-4" /> Jump to Comparison Tool
                </button>
              </div>

              {/* Document Vault */}
              <button
                type="button"
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer text-left w-full"
                onClick={() => navigate('/customer/documents')}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Document Vault</h3>
                </div>
                <p className="text-sm text-gray-500">Upload and share documents securely with your broker</p>
                <span className="inline-block mt-3 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">Open Vault</span>
              </button>

              {/* Messages */}
              <button
                type="button"
                onClick={() => navigate('/messages')}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow text-left w-full"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-rose-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-rose-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Messages</h3>
                </div>
                <p className="text-sm text-gray-500">Stay in touch with your broker via messages</p>
                <span className="inline-block mt-3 text-xs font-medium text-rose-600 bg-rose-50 px-2 py-1 rounded">Open Messages</span>
              </button>

              {/* Saved Comparisons */}
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <Search className="w-5 h-5 text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Saved Comparisons</h3>
                </div>
                {savedComparisons.length === 0 ? (
                  <p className="text-sm text-gray-500">Run a comparison and save it to revisit later</p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {savedComparisons.slice(0, 5).map(sc => (
                      <div key={sc.id} className="flex items-center justify-between text-sm bg-teal-50 rounded-lg px-3 py-2">
                        <button
                          onClick={() => handleRerunComparison(sc)}
                          className="text-teal-700 hover:text-teal-900 font-medium truncate text-left flex-1"
                          title="Click to re-run this comparison"
                        >
                          {sc.name}
                        </button>
                        <button
                          onClick={() => handleDeleteSavedComparison(sc.id)}
                          className="text-gray-400 hover:text-red-600 ml-2 shrink-0"
                        >
                          <span className="text-xs">x</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <span className="inline-block mt-3 text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded">
                  {savedComparisons.length} saved
                </span>
              </div>

              {/* Financial Profile Card (Epic 6 — full page) */}
              <button
                type="button"
                onClick={() => navigate('/customer/financial-profile')}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all cursor-pointer text-left w-full"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Financial Profile</h3>
                </div>
                {cashFlowSummary && (cashFlowSummary.income_count > 0 || cashFlowSummary.expense_count > 0) ? (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-600">
                      Income: <span className="font-medium text-green-700">${cashFlowSummary.total_monthly_income.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo</span>
                      {' · '}
                      Expenses: <span className="font-medium text-red-700">${cashFlowSummary.total_monthly_expenses.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo</span>
                    </p>
                    <p className="text-xs text-gray-600">
                      Net: <span className={`font-medium ${cashFlowSummary.net_monthly_cashflow >= 0 ? 'text-green-700' : 'text-red-700'}`}>${cashFlowSummary.net_monthly_cashflow.toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo</span>
                    </p>
                    {myConnection?.status === 'active' && (
                      <p className="text-xs mt-1">
                        {financialsShared
                          ? <span className="text-green-600">Shared with broker</span>
                          : <span className="text-amber-600">Not shared with broker</span>}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Complete your income, expenses, liabilities & assets</p>
                )}
                <span className="inline-block mt-3 text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded">Open Profile</span>
              </button>
            </div>

            {/* Divider before comparison tool */}
            <div id="compare-section" className="border-t border-gray-200 pt-8 mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Compare Home Loans</h2>
              <p className="text-gray-600 mt-2">
                Find the best mortgage rate for your needs from Australia's top lenders
              </p>
            </div>
          </>
        )}

        {/* Guest header (not authenticated) */}
        {(!isAuthenticated || user?.userType !== 'customer') && (
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Compare Home Loans</h1>
            <p className="text-gray-600 mt-2">
              Find the best mortgage rate for your needs from Australia's top lenders
            </p>
          </div>
        )}

        {/* Comparison Form */}
        <div className="card mb-8">
          <form onSubmit={handleCompare}>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="loanAmount" className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Loan Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="loanAmount"
                    value={formatNumberWithCommas(loanAmount)}
                    onChange={(e) => setLoanAmount(stripToNumber(e.target.value))}
                    className="pl-8 w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                    placeholder="500,000"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="propertyValue" className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Property Value
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-3 text-gray-500">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="propertyValue"
                    value={formatNumberWithCommas(propertyValue)}
                    onChange={(e) => setPropertyValue(stripToNumber(e.target.value))}
                    className="pl-8 w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                    placeholder="700,000"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="loanTerm" className="block text-sm font-semibold text-gray-800 mb-1.5">
                  Loan Term (Years)
                </label>
                <input
                  type="number"
                  id="loanTerm"
                  value={loanTerm}
                  onChange={(e) => setLoanTerm(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 shadow-sm"
                  placeholder="30"
                  min="1"
                  max="40"
                  required
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600">
{results.length > 0 && (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-sm text-gray-500">LVR:</span>
                    <span className={`inline-flex items-center text-sm font-bold px-2.5 py-0.5 rounded-full ${
                      lvr < 80 ? 'bg-green-100 text-green-700' :
                      lvr <= 90 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>{lvr.toFixed(2)}%</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && !filtersOpen ? 'Comparing...' : 'Compare Loans'}
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setFiltersOpen(!filtersOpen)}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium border border-gray-300 hover:border-primary-500 rounded-lg text-gray-700 hover:text-primary-600 transition-colors disabled:opacity-50"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  <span>Advanced Search</span>
                  {activeFilterCount > 0 && (
                    <span className="bg-primary-600 text-white text-xs px-2 py-0.5 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Advanced Search Panel (Epic 9) — outside form, only visible when toggled */}
          {filtersOpen && (
            <div className="mt-4 p-6 bg-white rounded-xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">Refine by Your Situation</h3>
                <button
                  type="button"
                  onClick={() => setFiltersOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                  aria-label="Close advanced search"
                >
                  &times;
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {CUSTOMER_FILTER_CATEGORIES.map(catKey => {
                  const cat = POLICY_TAG_CATEGORIES[catKey]
                  const selected = policyFilters[catKey]?.[0] || ''
                  return (
                    <div key={catKey}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{cat.label}</label>
                      <select
                        value={selected}
                        onChange={(e) => setPolicyDropdown(catKey, e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Any</option>
                        {cat.tags.map(tag => (
                          <option key={tag.value} value={tag.value}>{tag.label}</option>
                        ))}
                      </select>
                    </div>
                  )
                })}
              </div>

              {/* Special Eligibility — grouped sub-sections */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-3">Special Eligibility</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {CUSTOMER_CHECKBOX_CATEGORIES.map(catKey => {
                    const cat = POLICY_TAG_CATEGORIES[catKey]
                    return (
                      <div key={catKey}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{cat.label}</p>
                        <div className="space-y-1.5">
                          {cat.tags.map(tag => {
                            const checked = (policyFilters[catKey] || []).includes(tag.value)
                            return (
                              <label key={tag.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => togglePolicyCheckbox(catKey, tag.value, e.target.checked)}
                                  className="w-3.5 h-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                {tag.label}
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                {activeFilterCount > 0 ? (
                  <button
                    type="button"
                    onClick={clearAllPolicyFilters}
                    className="text-sm text-red-500 hover:text-red-700 underline"
                  >
                    Clear all filters
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">Select filters to refine your search</span>
                )}
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleAdvancedCompare}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && filtersOpen ? 'Comparing...' : 'Compare with Filters'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}
        </div>

        {/* Loading Skeleton */}
        {loading && (
          <div className="card">
            <div className="mb-6">
              <div className="h-7 w-48 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="p-6 border-2 border-gray-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                    <div className="flex-1">
                      <div className="h-5 w-56 bg-gray-200 rounded animate-pulse mb-2" />
                      <div className="h-4 w-36 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="bg-gray-100 rounded-lg p-3">
                        <div className="h-7 w-20 bg-gray-200 rounded animate-pulse mx-auto mb-2" />
                        <div className="h-3 w-14 bg-gray-200 rounded animate-pulse mx-auto" />
                      </div>
                    ))}
                  </div>
                  <div className="h-10 w-52 bg-gray-200 rounded animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {results.length > 0 && !loading && (
          <div className="card">
            <div className="mb-6">
              <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {sortedResults.length} Loan{sortedResults.length !== 1 ? 's' : ''} Found
                  </h2>
                  {sortedResults.length < results.length && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      (filtered from {results.length})
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* View Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-0.5">
                    <button
                      onClick={() => setViewMode('card')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'card' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <LayoutGrid className="w-4 h-4" />
                      Cards
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-900'
                      }`}
                    >
                      <ListIcon className="w-4 h-4" />
                      List
                    </button>
                  </div>
                  {isAuthenticated && user?.userType === 'customer' && (
                    <button
                      onClick={() => setShowSaveModal(true)}
                      className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 px-3 py-1.5 border border-teal-300 rounded-lg bg-teal-50 hover:bg-teal-100 transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Save
                    </button>
                  )}
                </div>
              </div>


              {/* Sort pills */}
              <div className="flex flex-wrap gap-2 mb-4 items-center">
                <span className="text-sm text-gray-500 font-medium self-center mr-1">SORT:</span>
                {([
                  ['total_cost', 'Total Cost'],
                  ['interest_rate', 'Interest Rate'],
                  ['monthly_payment', 'Monthly Payment'],
                  ['comparison_rate', 'Comparison Rate'],
                  ['total_fees', 'Fees'],
                ] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setSortBy(key)}
                    className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                      sortBy === key
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
                <span className="text-gray-300 mx-1 self-center">|</span>
                <button
                  onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                  className="text-sm text-gray-600 hover:text-primary-600 flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg shadow-sm"
                >
                  {sortDir === 'asc' ? '↑ Low to High' : '↓ High to Low'}
                </button>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Types</option>
                  {availableTypes.map(t => (
                    <option key={t} value={t}>{getTypeLabel(t)}</option>
                  ))}
                </select>
                <select
                  value={filterBank}
                  onChange={(e) => setFilterBank(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Banks</option>
                  {availableBanks.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
                <span className="text-gray-300">|</span>
                {['offset_account', 'redraw_facility', 'extra_repayments', 'portability'].map(feat => {
                  const label = feat.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                  const active = filterFeatures.includes(feat)
                  return (
                    <button
                      key={feat}
                      onClick={() => setFilterFeatures(prev =>
                        active ? prev.filter(f => f !== feat) : [...prev, feat]
                      )}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                        active
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {active ? <Check className="w-3.5 h-3.5 inline mr-1" /> : null}
                      {label}
                    </button>
                  )
                })}
                {(filterType !== 'all' || filterBank !== 'all' || filterFeatures.length > 0) && (
                  <button
                    onClick={() => { setFilterType('all'); setFilterBank('all'); setFilterFeatures([]) }}
                    className="text-sm text-red-500 hover:text-red-700 underline"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {/* Eligibility count summary */}
              {hasSearchedWithFilters && sortedResults.some(r => r.policy_match) && (
                <div className="flex items-center gap-3 text-sm mt-4 px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                  <span className="flex items-center gap-1.5 text-green-700 font-medium">
                    <Check className="w-4 h-4" />
                    {sortedResults.filter(r => r.policy_match === 'eligible').length} eligible
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="flex items-center gap-1.5 text-amber-600 font-medium">
                    <HelpCircle className="w-4 h-4" />
                    {sortedResults.filter(r => r.policy_match === 'may_be_eligible').length} may be eligible
                  </span>
                </div>
              )}
            </div>

            {/* IMPORTANT RATE DISCLAIMER — Collapsible */}
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-gray-700 flex-1">
                  <strong>Rates shown are indicative only.</strong> Verify with lenders before making decisions.
                </p>
                <button
                  onClick={() => setDisclaimerExpanded(!disclaimerExpanded)}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap ml-2"
                >
                  {disclaimerExpanded ? 'Hide disclaimer ▲' : 'Read full disclaimer ▼'}
                </button>
              </div>
              {disclaimerExpanded && (
                <div className="mt-3 pt-3 border-t border-yellow-300">
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Rates based on published information as of today's date</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Actual rates may vary based on your circumstances, credit history, LVR, and lender criteria</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Lenders may change rates at any time without notice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>This is a comparison tool only — LoanLoop does not provide lending services or financial advice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>No guarantee of approval or rate availability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>LoanLoop is not liable for rate changes, offer modifications, or application outcomes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-yellow-600">•</span>
                      <span>Always verify current rates directly with lenders before making any decisions</span>
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-gray-600">
                    <strong>Not financial advice.</strong> Seek independent professional advice before making financial decisions.{' '}
                    <Link to="/legal/disclaimer" className="text-primary-600 hover:text-primary-700 underline">
                      Full legal disclaimer
                    </Link>
                  </p>
                </div>
              )}
            </div>

            {/* CARD VIEW */}
            {viewMode === 'card' && (
            <div className="space-y-4">
              {sortedResults.map((result, index) => {
                const bankName = getBankName(result.product_name)
                const loanName = result.product_name.includes(' - ') ? result.product_name.split(' - ').slice(1).join(' - ') : result.product_name
                const highestCost = sortedResults[sortedResults.length - 1]?.total_cost || result.total_cost
                const savings = highestCost - result.total_cost

                return (
                  <div
                    key={index}
                    className={`p-6 border-2 rounded-2xl shadow-sm transition-all hover:shadow-lg hover:border-blue-400 ${
                      index === 0 ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div>
                    {/* Header: Bank identity + badge */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-bold text-gray-600 flex-shrink-0 overflow-hidden">
                          {!faviconErrors[bankName] && getBankDomain(result.product_name) ? (
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${getBankDomain(result.product_name)}&sz=64`}
                              alt={bankName}
                              className="w-8 h-8 object-contain"
                              onError={() => setFaviconErrors(prev => ({ ...prev, [bankName]: true }))}
                            />
                          ) : (
                            bankName.slice(0, 3).toUpperCase()
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{bankName}</h3>
                          <p className="text-sm text-gray-500">{loanName}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {index === 0 && (
                          <span className="inline-block px-3 py-1 bg-primary-600 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                            Best Value
                          </span>
                        )}
                        {result.id === lowestRateId && index !== 0 && (
                          <span className="inline-block px-3 py-1 bg-green-600 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                            Lowest Rate
                          </span>
                        )}
                        {result.id === lowestPaymentId && index !== 0 && (
                          <span className="inline-block px-3 py-1 bg-teal-600 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                            Lowest Payment
                          </span>
                        )}
                        {hasSearchedWithFilters && result.policy_match === 'eligible' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full whitespace-nowrap">
                            <Check className="w-3.5 h-3.5" /> Matches your situation
                          </span>
                        )}
                        {hasSearchedWithFilters && result.policy_match === 'may_be_eligible' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 text-sm font-semibold rounded-full whitespace-nowrap">
                            <HelpCircle className="w-3.5 h-3.5" /> May be eligible
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Key metrics grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                        <div className="text-3xl font-bold text-primary-600">
                          {result.interest_rate.toFixed(2)}%
                        </div>
                        <div className="text-xs text-gray-500 mt-1">p.a. Rate</div>
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatCurrency(result.monthly_payment)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">/month</div>
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatCurrency(result.total_cost)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">total cost</div>
                      </div>
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {formatCurrency(result.total_fees)}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">in fees</div>
                      </div>
                    </div>

                    {/* Comparison rate */}
                    <div className="mt-3 mb-3">
                      <span className="inline-block bg-gray-100 text-gray-500 text-sm px-3 py-1 rounded-full">Comparison rate: {result.comparison_rate.toFixed(2)}% p.a.</span>
                    </div>

                    {/* Features */}
                    {Object.keys(result.features).length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {result.features.offset_account && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full border border-green-200">
                            <Check className="w-3.5 h-3.5" /> Offset
                          </span>
                        )}
                        {result.features.redraw_facility && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full border border-green-200">
                            <Check className="w-3.5 h-3.5" /> Redraw
                          </span>
                        )}
                        {result.features.extra_repayments && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full border border-green-200">
                            <Check className="w-3.5 h-3.5" /> Extra Repay.
                          </span>
                        )}
                        {result.features.portability && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full border border-green-200">
                            <Check className="w-3.5 h-3.5" /> Portable
                          </span>
                        )}
                      </div>
                    )}

                    {/* CTA + savings */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                      <button
                        onClick={() => {
                          if (myConnection?.status === 'active' && isAuthenticated && user) {
                            setExpandedShareLoan(expandedShareLoan === (result.id ?? '') ? null : (result.id ?? ''))
                          } else {
                            setSelectedLoan(result)
                            setIsModalOpen(true)
                          }
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-xl inline-flex items-center gap-1.5 justify-center w-full sm:w-auto"
                      >
                        {myConnection?.status === 'active'
                          ? `Share with ${myConnection.broker_name || 'Your Broker'}`
                          : 'Enquire About This Loan'}
                        {!(myConnection?.status === 'active') && <ArrowRight className="w-4 h-4" />}
                      </button>
                      {savings > 0 && sortedResults.length > 1 && (
                        <span className="text-sm text-green-700 font-medium inline-flex items-center gap-1.5">
                          <Banknote className="w-4 h-4 text-green-600" />
                          Save {formatCurrency(savings)} vs highest-cost option
                        </span>
                      )}
                    </div>

                    {expandedShareLoan === result.id && myConnection?.status === 'active' && isAuthenticated && user && (
                      <ShareWithBrokerCard
                        loanDetails={{
                          bank: bankName,
                          product_name: result.product_name,
                          interest_rate: result.interest_rate,
                          comparison_rate: result.comparison_rate,
                          monthly_payment: result.monthly_payment,
                          total_cost: result.total_cost,
                          loan_term_years: parseInt(loanTerm),
                          loan_amount: parseFloat(loanAmount),
                          property_value: parseFloat(propertyValue),
                        }}
                        loanProductId={result.id}
                        brokerName={myConnection.broker_name || 'Your Broker'}
                        user={{ name: user.full_name || '', email: user.email || '', phone: customerPhone }}
                        onSent={() => setExpandedShareLoan(null)}
                        onCancel={() => setExpandedShareLoan(null)}
                      />
                    )}
                    </div>
                  </div>
                )
              })}
            </div>
            )}

            {/* LIST VIEW */}
            {viewMode === 'list' && (
              <div className="space-y-3">
                {sortedResults.map((result, index) => {
                  const bankName = getBankName(result.product_name)
                  const loanName = result.product_name.includes(' - ') ? result.product_name.split(' - ').slice(1).join(' - ') : result.product_name
                  const highestCost = sortedResults[sortedResults.length - 1]?.total_cost || result.total_cost
                  const savings = highestCost - result.total_cost

                  return (
                    <div
                      key={index}
                      className={`bg-white rounded-lg px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-4 transition-all hover:shadow-md hover:border-blue-400 ${
                        index === 0 ? 'border-2 border-blue-500' : 'border border-gray-200'
                      }`}
                    >
                      {/* Bank info */}
                      <div className="flex items-center gap-3 lg:w-[260px] flex-shrink-0">
                        <div className="w-10 h-10 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {!faviconErrors[bankName] && getBankDomain(result.product_name) ? (
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${getBankDomain(result.product_name)}&sz=64`}
                              alt={bankName}
                              className="w-6 h-6 object-contain"
                              onError={() => setFaviconErrors(prev => ({ ...prev, [bankName]: true }))}
                            />
                          ) : (
                            <span className="text-xs font-bold text-gray-700">{bankName.slice(0, 3).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">{bankName}</p>
                          <p className="text-xs text-gray-500 truncate">{loanName}</p>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 flex-1">
                        <div><p className="text-lg font-extrabold text-primary-600">{result.interest_rate.toFixed(2)}%</p><p className="text-[10px] text-gray-400">Rate</p></div>
                        <div><p className="text-lg font-extrabold text-gray-900">{formatCurrency(result.monthly_payment)}</p><p className="text-[10px] text-gray-400">/month</p></div>
                        <div><p className="text-lg font-extrabold text-gray-900">{formatCurrency(result.total_cost)}</p><p className="text-[10px] text-gray-400">Total cost</p></div>
                        <div><p className="text-lg font-extrabold text-gray-900">{formatCurrency(result.total_fees)}</p><p className="text-[10px] text-gray-400">Fees</p></div>
                        <div className="text-[10px] text-gray-400">Comp: {result.comparison_rate.toFixed(2)}%</div>
                        {savings > 0 && sortedResults.length > 1 && (
                          <span className="text-[10px] text-green-700 font-medium inline-flex items-center gap-1">
                            <Banknote className="w-3 h-3 text-green-600" />
                            Save {formatCurrency(savings)}
                          </span>
                        )}
                      </div>

                      {/* Badges */}
                      <div className="flex flex-wrap items-center gap-2 lg:w-auto flex-shrink-0">
                        {index === 0 && (
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-primary-600 text-white">Best Value</span>
                        )}
                        {hasSearchedWithFilters && result.policy_match === 'eligible' && (
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-green-100 text-green-800">Eligible</span>
                        )}
                        {hasSearchedWithFilters && result.policy_match === 'may_be_eligible' && (
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800">May be eligible</span>
                        )}
                        {result.features.offset_account && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700">Offset</span>}
                        {result.features.redraw_facility && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700">Redraw</span>}
                        {result.features.extra_repayments && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700">Extra Repay.</span>}
                        {result.features.portability && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700">Portable</span>}
                      </div>

                      {/* CTA */}
                      <button
                        onClick={() => {
                          if (myConnection?.status === 'active' && isAuthenticated && user) {
                            setExpandedShareLoan(expandedShareLoan === (result.id ?? '') ? null : (result.id ?? ''))
                          } else {
                            setSelectedLoan(result)
                            setIsModalOpen(true)
                          }
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg text-sm whitespace-nowrap text-center flex-shrink-0 transition-colors"
                      >
                        {myConnection?.status === 'active'
                          ? `Share with ${myConnection.broker_name || 'Broker'}`
                          : 'Enquire →'}
                      </button>

                      {/* Share with broker card (expanded in list view) */}
                      {expandedShareLoan === result.id && myConnection?.status === 'active' && isAuthenticated && user && (
                        <div className="mt-3 w-full">
                          <ShareWithBrokerCard
                            loanDetails={{
                              bank: bankName,
                              product_name: result.product_name,
                              interest_rate: result.interest_rate,
                              comparison_rate: result.comparison_rate,
                              monthly_payment: result.monthly_payment,
                              total_cost: result.total_cost,
                              loan_term_years: parseInt(loanTerm),
                              loan_amount: parseFloat(loanAmount),
                              property_value: parseFloat(propertyValue),
                            }}
                            loanProductId={result.id}
                            brokerName={myConnection.broker_name || 'Your Broker'}
                            user={{ name: user.full_name || '', email: user.email || '', phone: customerPhone }}
                            onSent={() => setExpandedShareLoan(null)}
                            onCancel={() => setExpandedShareLoan(null)}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* No-match CTA when filters active but 0 results */}
            {sortedResults.length === 0 && hasSearchedWithFilters && !loading && (
              <div className="mt-6 p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <HelpCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No exact matches found</h3>
                <p className="text-sm text-gray-600 mb-4">
                  No products in our database match all your selected criteria. A mortgage broker can help find options for your situation.
                </p>
                <button
                  type="button"
                  onClick={() => { setSelectedLoan(null); setIsModalOpen(true) }}
                  className="btn-primary"
                >
                  Talk to a Broker
                </button>
              </div>
            )}

            {/* Savings Summary */}
            {sortedResults.length > 1 && (
              <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-green-900">Potential Savings</h3>
                    <p className="text-green-700">
                      By choosing the best option vs. the highest cost loan
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(
                        sortedResults[sortedResults.length - 1].total_cost - sortedResults[0].total_cost
                      )}
                    </div>
                    <div className="text-sm text-green-700">over {loanTerm} years</div>
                  </div>
                </div>
              </div>
            )}
          {/* CTA Banner — Get help from a broker (D8) */}
            <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Search className="w-8 h-8 text-blue-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {myConnection?.status === 'active' ? 'Share results with your broker' : 'Need help choosing?'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {myConnection?.status === 'active'
                        ? `Send your top loan pick to ${myConnection.broker_name || 'your broker'} for expert advice.`
                        : 'Get expert advice from a mortgage broker at no cost to you.'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  {myConnection?.status === 'active' ? (
                    <button
                      onClick={() => {
                        if (sortedResults.length > 0) {
                          setExpandedShareLoan(expandedShareLoan === (sortedResults[0].id ?? '') ? null : (sortedResults[0].id ?? ''))
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Share with {myConnection.broker_name || 'Your Broker'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          if (sortedResults.length > 0) {
                            setSelectedLoan(sortedResults[0])
                            setIsModalOpen(true)
                          }
                        }}
                        className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium"
                      >
                        Enquire Without Signing Up
                      </button>
                      <Link to="/brokers" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                        Connect With a Broker
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}


        {/* No results from basic search — friendly broker CTA */}
        {results.length === 0 && hasSearched && !hasSearchedWithFilters && !loading && (
          <div className="card py-10 text-center">
            <div className="text-5xl mb-4">🏠🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Well, that's a tricky one!
            </h3>
            <p className="text-gray-600 mb-1 max-w-md mx-auto">
              Looks like our database couldn't find a match for your loan — but don't worry,
              that doesn't mean it doesn't exist!
            </p>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              A mortgage broker eats these challenges for breakfast. They have access to
              lenders and products we haven't listed yet, and they love a good puzzle.
            </p>
            <button
              type="button"
              onClick={() => { setSelectedLoan(null); setIsModalOpen(true) }}
              className="btn-primary text-base px-6 py-3"
            >
              Get a Broker on the Case
            </button>
            <p className="text-xs text-gray-400 mt-3">Free service — no obligation</p>
          </div>
        )}

        {/* Empty State — Sample comparison preview */}
        {!loading && results.length === 0 && !hasSearched && (
          <div className="card py-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
              Enter your details to see real results
            </h3>
            <p className="text-gray-500 text-center mb-6 text-sm">
              Here's what a typical comparison looks like
            </p>
            <div className="space-y-3 opacity-60 pointer-events-none select-none">
              {[
                { bank: 'WES', domain: 'westpac.com.au', name: 'Fixed 3 Year', type: 'fixed_3', rate: '5.49%', payment: '$2,834', cost: '$1,020,240', badge: 'Best Value' },
                { bank: 'CBA', domain: 'commbank.com.au', name: 'Variable Rate', type: 'variable', rate: '5.99%', payment: '$2,997', cost: '$1,078,920', badge: null },
                { bank: 'ANZ', domain: 'anz.com.au', name: 'Fixed 2 Year', type: 'fixed_2', rate: '5.69%', payment: '$2,902', cost: '$1,044,720', badge: 'Lowest Rate' },
              ].map((sample, i) => (
                <div key={i} className={`p-4 border-2 rounded-lg ${i === 0 ? 'border-primary-300 bg-primary-50/50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-xs font-bold text-gray-500 overflow-hidden">
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${sample.domain}&sz=64`}
                          alt={sample.bank}
                          className="w-7 h-7 object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling!.removeAttribute('style') }}
                        />
                        <span style={{ display: 'none' }}>{sample.bank}</span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-700">{sample.name}</div>
                        <div className="text-xs text-gray-400">{sample.type}</div>
                      </div>
                    </div>
                    {sample.badge && (
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${i === 0 ? 'bg-primary-200 text-primary-700' : 'bg-green-200 text-green-700'}`}>
                        {sample.badge}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-gray-100 rounded p-2 text-center">
                      <div className="text-lg font-bold text-gray-500">{sample.rate}</div>
                      <div className="text-xs text-gray-400">p.a.</div>
                    </div>
                    <div className="bg-gray-100 rounded p-2 text-center">
                      <div className="text-lg font-bold text-gray-500">{sample.payment}</div>
                      <div className="text-xs text-gray-400">/month</div>
                    </div>
                    <div className="bg-gray-100 rounded p-2 text-center">
                      <div className="text-lg font-bold text-gray-500">{sample.cost}</div>
                      <div className="text-xs text-gray-400">total</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-primary-600 font-medium mt-4">
              Real-time rates from Australia's top lenders in under 1 second
            </p>
          </div>
        )}
      </div>
      
      {/* Save Comparison Modal (Epic 6) */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Save Comparison</h3>
            <input
              type="text"
              placeholder="Give it a name (optional)"
              value={saveComparisonName}
              onChange={e => setSaveComparisonName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 mb-3 text-sm"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <p className="text-sm text-gray-500 mb-4">
              Saving {results.length} loan{results.length !== 1 ? 's' : ''} with current criteria
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowSaveModal(false); setSaveComparisonName('') }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveComparison}
                disabled={savingComparison}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:bg-teal-300"
              >
                {savingComparison ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lead Capture Modal */}
      {isModalOpen && (
        <LeadCaptureModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedLoan(null)
          }}
          loanDetails={selectedLoan ? {
            bank: selectedLoan.product_name.split(' - ')[0] || 'Unknown Bank',
            product_name: selectedLoan.product_name,
            interest_rate: selectedLoan.interest_rate,
            comparison_rate: selectedLoan.comparison_rate,
            monthly_payment: selectedLoan.monthly_payment,
            total_cost: selectedLoan.total_cost,
            loan_term_years: parseInt(loanTerm),
            loan_amount: parseFloat(loanAmount),
            property_value: parseFloat(propertyValue),
          } : {
            bank: '',
            product_name: 'General enquiry',
            interest_rate: 0,
            loan_term_years: parseInt(loanTerm),
            loan_amount: parseFloat(loanAmount),
            property_value: parseFloat(propertyValue),
          }}
          loanProductId={selectedLoan?.id}
          user={isAuthenticated && user ? { full_name: user.full_name, email: user.email, phone: customerPhone } : undefined}
          onSignInClick={
            !isAuthenticated
              ? () => {
                  const loanDetails = selectedLoan ? {
                    bank: selectedLoan.product_name.split(' - ')[0] || 'Unknown Bank',
                    product_name: selectedLoan.product_name,
                    interest_rate: selectedLoan.interest_rate,
                    comparison_rate: selectedLoan.comparison_rate,
                    monthly_payment: selectedLoan.monthly_payment,
                    total_cost: selectedLoan.total_cost,
                    loan_term_years: parseInt(loanTerm),
                    loan_amount: parseFloat(loanAmount),
                    property_value: parseFloat(propertyValue),
                  } : {
                    bank: '',
                    product_name: 'General enquiry',
                    interest_rate: 0,
                    loan_term_years: parseInt(loanTerm),
                    loan_amount: parseFloat(loanAmount),
                    property_value: parseFloat(propertyValue),
                  }
                  sessionStorage.setItem(
                    'pending_lead_capture',
                    JSON.stringify({
                      loanDetails,
                      loanProductId: selectedLoan?.id,
                      selectedLoan,
                      customerSituation: buildSituationContext(policyFilters),
                    })
                  )
                  navigate('/login?returnTo=/customer')
                }
              : undefined
          }
          customerSituation={buildSituationContext(policyFilters)}
        />
      )}


    </div>
  )
}
